import type {HeaderData} from '@commercial-website/data-layer'
import {GatsbyClient} from '@commercial-website/data-layer/client'
import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'

import {topNavigationSignupActioned, TopNavigationSignupActionedAction} from '@/analytics/segment'
import {LocalizedLink, TrackedLocalizedLink} from '@/components/localized-link'
import type {Props as SignupProps} from '@/components/signup-modal'
import type {HeaderQuery} from '@/graphql-types'
import useHasMounted from '@/hooks/use-has-mounted'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {useLocale} from '@/providers/locale'
import {usePageInfo} from '@/providers/page-info'
import {useSignupModal} from '@/providers/signup-modal'

import {HeaderV2} from './header-v2'
import {mapPrismicHeaderV2} from './map-prismic-header-v2'

const AVAILABILITY_TEST_ID = 'availability-test'

export interface Props {
    hideHeader?: boolean
    logoOnly?: boolean
    ctaDestination?: 'signupform' | 'demopage' | 'partnersform' | 'accountingpartners'
    exploreProductDestination?: 'explorepartnerportal' | 'exploreproduct'
    genericBookADemo?: boolean
}

const TRACKED_ELEMENT_LOCATION = 'navbar header'

type CtaButtonParams = {
    ctaDestination: Props['ctaDestination']
    currentLocale: {code: string}
    text: string
    location: {pathname: string} | null | undefined
    onSignupCtaClick: (signupProps?: SignupProps) => void
    className?: string
    disabled?: boolean
}

// The header (rebrand `ButtonLink`, legacy `NavigationMenu.Link`) already supplies the full CTA
// styling via `className`. Wrapping this in a Button component layers a second set of variant
// classes on top, which twMerge cannot strip where the two sets don't overlap (e.g. the legacy
// `hover:bg-*` survives because the rebrand pink variant has no hover background).
const buildCtaButton = ({
    ctaDestination,
    currentLocale,
    text,
    location,
    onSignupCtaClick,
    disabled,
    className,
}: CtaButtonParams): React.ReactElement => {
    if (currentLocale.code === 'en-us') {
        return (
            <TrackedLocalizedLink
                className={className}
                to="/getting-started"
                trackingData={{
                    linkText: text,
                    linkType: 'cta',
                    elementLocation: TRACKED_ELEMENT_LOCATION,
                }}
                state={{prevPath: location?.pathname}}
            >
                {text}
            </TrackedLocalizedLink>
        )
    }

    switch (ctaDestination) {
        case 'signupform': {
            return (
                <button
                    type="button"
                    className={className}
                    onClick={() => onSignupCtaClick()}
                    aria-disabled={disabled}
                >
                    {text}
                </button>
            )
        }

        case 'accountingpartners': {
            return (
                <button
                    type="button"
                    className={className}
                    onClick={() =>
                        onSignupCtaClick({
                            signupProps: {
                                form: 'Partner',
                                isPartner: true,
                            },
                        })
                    }
                    aria-disabled={disabled}
                >
                    {text}
                </button>
            )
        }

        case 'demopage': {
            return (
                <TrackedLocalizedLink
                    className={className}
                    to="/demo"
                    trackingData={{
                        linkText: text,
                        linkType: 'cta',
                        elementLocation: TRACKED_ELEMENT_LOCATION,
                    }}
                >
                    {text}
                </TrackedLocalizedLink>
            )
        }

        case 'partnersform': {
            return (
                <LocalizedLink
                    className={className}
                    to="/bigform-partner"
                    onClick={() =>
                        topNavigationSignupActioned({
                            action: TopNavigationSignupActionedAction.Partners,
                        })
                    }
                >
                    {text}
                </LocalizedLink>
            )
        }

        default: {
            throw new Error('Unrecognized destination')
        }
    }
}

const HeaderContainer = ({hideHeader, ...props}: Props) => {
    const data = useStaticQuery<HeaderQuery>(query)
    const [content] = useLocalizedContent(data.allPrismicHeader.nodes)
    const [contentV2] = useLocalizedContent(data.allPrismicHeaderV2.nodes, {
        fallbackToDefaultLocale: false,
    })

    const hasMounted = useHasMounted()
    const currentLocale = useLocale()
    const {location} = usePageInfo()
    const {renderSignupModal} = useSignupModal()

    // GatsbyClient.getHeader throws when a secondary locale has no static content, which replaces the
    // explicit guard that used to live here.
    const secondaryHeader = currentLocale.isSecondary
        ? GatsbyClient.getHeader(currentLocale.code)
        : undefined

    if (!currentLocale.isSecondary && !contentV2) {
        throw new Error(`Missing Prismic HeaderV2 content for locale ${currentLocale.code}`)
    }

    const headerV2Content: HeaderData = secondaryHeader ?? mapPrismicHeaderV2(contentV2!.data)

    const ctaButtonText = secondaryHeader
        ? secondaryHeader.ctaButtonText
        : content.data.start_free_trial

    const onSignupCtaClick = (signupProps?: SignupProps) => {
        renderSignupModal(signupProps)
        topNavigationSignupActioned({action: TopNavigationSignupActionedAction.Started})
    }

    if (hideHeader) {
        return null
    }

    return (
        <HeaderV2
            content={headerV2Content}
            ctaButton={({className}) =>
                buildCtaButton({
                    ctaDestination: props.ctaDestination ?? 'signupform',
                    currentLocale,
                    text: ctaButtonText,
                    location,
                    onSignupCtaClick,
                    className,
                    disabled: !hasMounted,
                })
            }
            logoOnly={props.logoOnly}
            testId={AVAILABILITY_TEST_ID}
        />
    )
}

const query = graphql`
    query Header {
        allPrismicHeader {
            nodes {
                lang
                data {
                    start_free_trial
                }
            }
        }
        allPrismicHeaderV2 {
            nodes {
                lang
                data {
                    logInText: log_in
                    secondaryCtaText: secondary_cta_text
                    secondaryCtaLink: secondary_cta_link {
                        ...PrismicLink
                    }
                    noResultsText: no_results_text
                    items {
                        label
                        link {
                            ...PrismicLink
                        }
                        dropdown {
                            document {
                                ... on PrismicHeaderV2Dropdown {
                                    data {
                                        body {
                                            ... on PrismicHeaderV2DropdownDataBodyLinksGroup {
                                                primary {
                                                    label
                                                }
                                                items {
                                                    text
                                                    sub_text
                                                    link {
                                                        ...PrismicLink
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        hide_on
                    }
                }
            }
        }
    }
`

export default HeaderContainer
